import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Mail, Send, CheckCircle, XCircle, RefreshCw, Search,
  Users, Eye, GraduationCap, Shield, Trash2, Save, AlertCircle,
  Zap, Upload, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { key: 'parent', label: 'Parents', icon: Users, color: 'emerald' },
  { key: 'observer', label: 'Observers', icon: Eye, color: 'blue' },
  { key: 'principal', label: 'Principals', icon: GraduationCap, color: 'purple' },
  { key: 'admin', label: 'Admin', icon: Shield, color: 'orange' },
];

export default function AdminEmailSettings() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [activeRole, setActiveRole] = useState('parent');
  const [data, setData] = useState({ parent: [], observer: [], principal: [], admin: [] });
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState({}); // key=`${role}:${user_id}` → string

  const [testTo, setTestTo] = useState('');
  const [testSubject, setTestSubject] = useState('Sanjaya — test email');
  const [testMessage, setTestMessage] = useState('Hello from your Sanjaya Admin panel.');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [prefilling, setPrefilling] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [csvText, setCsvText] = useState('user_role,user_id,notification_email,label\n');
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const autoPrefill = async () => {
    setPrefilling(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/email/auto-prefill`, {}, authH);
      toast({
        title: 'Auto-fill complete',
        description: `Created/updated ${r.data.created_or_updated} mappings · ${r.data.skipped_existing} already set · ${r.data.errors} skipped`,
      });
      loadAll();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setPrefilling(false); }
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/email/digest/send-now`, {}, authH);
      if (r.data.error) {
        toast({ title: 'Cannot send', description: r.data.error, variant: 'destructive' });
      } else {
        toast({ title: 'Digest sent', description: `Delivered to ${r.data.sent} of ${r.data.recipients?.length || 0} admin(s)` });
      }
      loadAll();
    } catch (e) {
      toast({ title: 'Failed', variant: 'destructive' });
    } finally { setDigestSending(false); }
  };

  const importCsv = async () => {
    setCsvBusy(true); setCsvResult(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/email/mappings/csv-import`, { csv_text: csvText }, authH);
      setCsvResult(r.data);
      toast({ title: 'Import done', description: `+${r.data.created} new · ~${r.data.updated} updated · ${r.data.errors} errors` });
      loadAll();
    } catch (e) {
      setCsvResult({ error: e?.response?.data?.detail || 'Import failed' });
    } finally { setCsvBusy(false); }
  };

  const authH = { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } };

  const loadAll = async () => {
    try {
      const [s, u, l] = await Promise.all([
        axios.get(`${API_URL}/api/admin/email/status`, authH),
        axios.get(`${API_URL}/api/admin/email/users-needing-mapping`, authH),
        axios.get(`${API_URL}/api/admin/email/logs?limit=50`, authH),
      ]);
      setStatus(s.data);
      setData(u.data);
      setLogs(l.data.logs || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load email settings', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const saveMapping = async (row) => {
    const key = `${activeRole}:${row.user_id}`;
    const newEmail = (drafts[key] ?? row.notification_email ?? '').trim();
    if (!newEmail) {
      toast({ title: 'Empty email', description: 'Enter a notification email first', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/email/mappings`, {
        user_role: activeRole,
        user_id: row.user_id,
        login_email: row.login_email,
        notification_email: newEmail,
        active: true,
        label: row.label,
      }, authH);
      toast({ title: 'Saved', description: `Email mapped for ${row.name || row.user_id}` });
      setDrafts(d => { const c = { ...d }; delete c[key]; return c; });
      loadAll();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const deleteMapping = async (row) => {
    if (!window.confirm(`Remove notification email for ${row.name || row.user_id}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/email/mappings/${activeRole}/${row.user_id}`, authH);
      toast({ title: 'Removed' });
      loadAll();
    } catch (e) {
      toast({ title: 'Failed', variant: 'destructive' });
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) {
      toast({ title: 'Missing recipient', variant: 'destructive' });
      return;
    }
    setTestSending(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/email/test-send`, {
        to: testTo.trim(),
        subject: testSubject || 'Sanjaya — test email',
        message: testMessage,
      }, authH);
      setTestResult(r.data);
      toast({
        title: r.data.success ? 'Email sent' : 'Send failed',
        description: r.data.success ? `Delivered to ${testTo}` : (r.data.error || 'See result panel'),
        variant: r.data.success ? 'default' : 'destructive',
      });
      loadAll();
    } catch (e) {
      setTestResult({ success: false, error: e?.response?.data?.detail || e.message });
    } finally { setTestSending(false); }
  };

  const rows = data[activeRole] || [];
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      [r.name, r.login_email, r.notification_email, r.phone, r.user_id]
        .filter(Boolean).some(x => String(x).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const mappedCount = rows.filter(r => r.notification_email).length;
  const missingCount = rows.length - mappedCount;

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-email-root">
      {/* Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-600" />
            Email Notifications (Resend)
          </CardTitle>
          <CardDescription>Transactional email for tickets, reminders and digests.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <StatBlock label="Status">
              <span className={`font-semibold ${status?.configured ? 'text-emerald-600' : 'text-red-600'}`}>
                {status?.configured ? '✓ Connected' : '✗ Missing RESEND_API_KEY'}
              </span>
              {status?.api_key_preview && <div className="text-xs font-mono text-gray-400 mt-0.5">{status.api_key_preview}</div>}
            </StatBlock>
            <StatBlock label="Sender">
              <div className="font-mono text-xs">{status?.sender_email}</div>
              <div className="text-xs text-gray-500">{status?.sender_name}</div>
            </StatBlock>
            <StatBlock label="Admin Recipients">
              {(status?.admin_emails || []).length === 0
                ? <span className="text-amber-600 text-xs">⚠ Map at least one admin → notification email below</span>
                : status.admin_emails.map(e => <div key={e} className="text-xs font-mono">{e}</div>)}
            </StatBlock>
            <StatBlock label="Volume">
              <div><span className="font-semibold">{status?.mappings_count || 0}</span> mappings</div>
              <div className="text-xs text-gray-500"><span className="font-semibold">{status?.logs_count || 0}</span> total sends</div>
            </StatBlock>
          </div>
          {!status?.configured && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <strong>To activate:</strong> get an API key from <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a> (free 3,000/mo),
              add <code className="bg-amber-100 px-1 rounded">RESEND_API_KEY=re_...</code> and <code className="bg-amber-100 px-1 rounded">SENDER_EMAIL=noreply@yourdomain.com</code> to <code className="bg-amber-100 px-1 rounded">/app/backend/.env</code>, then restart backend.
            </div>
          )}

          {/* Power actions */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
            <Button size="sm" variant="outline" onClick={autoPrefill} disabled={prefilling} data-testid="email-auto-prefill">
              <Zap className="w-4 h-4 mr-1" />
              {prefilling ? 'Filling…' : 'Auto-fill from login emails'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowCsv(true)} data-testid="email-csv-import">
              <Upload className="w-4 h-4 mr-1" /> Import CSV
            </Button>
            <Button size="sm" variant="outline" onClick={sendDigestNow} disabled={digestSending} data-testid="email-digest-now">
              <Send className="w-4 h-4 mr-1" />
              {digestSending ? 'Sending…' : 'Send daily digest now'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test send */}
      <Card>
        <CardHeader>
          <CardTitle>Send a Test Email</CardTitle>
          <CardDescription>Validate your Resend connection. Branded HTML template will be used.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>To</Label>
              <Input type="email" placeholder="you@example.com" value={testTo} onChange={e => setTestTo(e.target.value)} data-testid="email-test-to" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={testSubject} onChange={e => setTestSubject(e.target.value)} data-testid="email-test-subject" />
            </div>
          </div>
          <div>
            <Label>Message body</Label>
            <textarea
              className="w-full border rounded p-2 text-sm min-h-[80px]"
              value={testMessage} onChange={e => setTestMessage(e.target.value)} data-testid="email-test-message"
            />
          </div>
          <Button onClick={sendTest} disabled={testSending} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="email-test-send">
            <Send className="w-4 h-4 mr-2" />
            {testSending ? 'Sending…' : 'Send Test'}
          </Button>
          {testResult && (
            <div className={`p-3 rounded border text-sm ${testResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 font-medium">
                {testResult.success
                  ? <><CheckCircle className="w-4 h-4 text-emerald-600" /> Sent (ID: <code className="text-xs">{testResult.id || '—'}</code>)</>
                  : <><XCircle className="w-4 h-4 text-red-600" /> {testResult.error || 'Failed'}</>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mappings */}
      <Card>
        <CardHeader>
          <CardTitle>User → Notification Email Mapping</CardTitle>
          <CardDescription>
            Login email stays unchanged. Notifications are sent to the email you set here. If empty, falls back to login email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Role tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {ROLES.map(r => {
              const Icon = r.icon;
              const cnt = (data[r.key] || []).length;
              const mapped = (data[r.key] || []).filter(u => u.notification_email).length;
              const active = activeRole === r.key;
              return (
                <button
                  key={r.key}
                  data-testid={`email-tab-${r.key}`}
                  onClick={() => { setActiveRole(r.key); setSearch(''); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                    active
                      ? `bg-${r.color}-100 text-${r.color}-700 border-${r.color}-300 shadow-sm`
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {r.label}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-white/80' : 'bg-gray-100'}`}>
                    {mapped}/{cnt}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Summary banner */}
          <div className="flex items-center justify-between mb-3 text-sm">
            <div className="flex items-center gap-3">
              <span className="text-gray-500">{mappedCount} mapped · </span>
              {missingCount > 0 ? (
                <span className="flex items-center gap-1 text-amber-700">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {missingCount} without a notification email
                </span>
              ) : (
                <span className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle className="w-3.5 h-3.5" /> All set
                </span>
              )}
            </div>
            <div className="relative w-64">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-8 h-9" placeholder="Search name / email / phone" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left text-xs text-gray-500">Name</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500">Login Email</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500">Notification Email</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500">Phone</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">
                    {rows.length === 0 ? 'No accounts yet for this role.' : 'No matches.'}
                  </td></tr>
                )}
                {filteredRows.map(row => {
                  const key = `${activeRole}:${row.user_id}`;
                  const value = drafts[key] ?? row.notification_email ?? '';
                  const dirty = drafts[key] !== undefined && drafts[key] !== (row.notification_email || '');
                  return (
                    <tr key={key} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{row.name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-600">{row.login_email || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2">
                        <Input
                          type="email"
                          className="h-8 text-sm"
                          placeholder={row.login_email ? 'Same as login (default)' : 'name@example.com'}
                          value={value}
                          onChange={e => setDrafts(d => ({ ...d, [key]: e.target.value }))}
                          data-testid={`email-input-${row.user_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-600">{row.phone || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button
                          size="sm"
                          onClick={() => saveMapping(row)}
                          disabled={!dirty}
                          className={`${dirty ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-200 text-gray-500'} text-white`}
                          data-testid={`email-save-${row.user_id}`}
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        {row.notification_email && (
                          <Button variant="ghost" size="sm" onClick={() => deleteMapping(row)} className="text-red-600" data-testid={`email-delete-${row.user_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent emails</CardTitle>
            <CardDescription>Last 50 attempts</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadAll}><RefreshCw className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent className="px-0">
          {logs.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">No emails sent yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-y">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">When</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">To</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Subject</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Event</th>
                    <th className="px-3 py-2 text-left text-xs text-gray-500">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-b">
                      <td className="px-3 py-2 text-xs text-gray-500">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono text-xs">{l.to}</td>
                      <td className="px-3 py-2 text-xs truncate max-w-xs">{l.subject}</td>
                      <td className="px-3 py-2 text-xs">{l.event_type}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${l.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {l.success ? 'Sent' : (l.error || 'Failed')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CSV import modal */}
      {showCsv && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCsv(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">Import notification emails (CSV)</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Required columns: <code className="bg-gray-100 px-1 rounded">user_role, user_id, notification_email</code> · optional: <code className="bg-gray-100 px-1 rounded">label, active</code>
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowCsv(false)}><X className="w-5 h-5" /></Button>
            </div>
            <textarea
              className="w-full border rounded p-3 text-xs font-mono min-h-[260px]"
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              data-testid="csv-textarea"
            />
            {csvResult && (
              <div className={`mt-3 p-3 rounded text-sm ${csvResult.error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                {csvResult.error || `✓ Created ${csvResult.created} · updated ${csvResult.updated} · errors ${csvResult.errors}`}
                {csvResult.errors_detail?.length > 0 && (
                  <pre className="text-xs mt-2 max-h-32 overflow-y-auto">{JSON.stringify(csvResult.errors_detail, null, 2)}</pre>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowCsv(false)}>Close</Button>
              <Button onClick={importCsv} disabled={csvBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="csv-import-submit">
                {csvBusy ? 'Importing…' : 'Import'}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Tip: To get user_ids quickly, click "Auto-fill from login emails" first — it bulk-creates all rows for you.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBlock({ label, children }) {
  return (
    <div>
      <p className="text-gray-500 text-xs uppercase">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}
